import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import {
  guardarProyeccionConfig,
  obtenerProyeccionConfig,
} from "@/modules/analisis_financiero/analisisFinancieroApi";
import ReferenciasTable from "@/modules/analisis_financiero/ReferenciasTable";

const MODOS_GASTO = [
  { id: "variacion_anio_anterior", etiqueta: "Variación mes a mes del año anterior" },
  { id: "pct_ventas", etiqueta: "Porcentaje de las ventas proyectadas" },
  { id: "variacion_propia", etiqueta: "Variación propia del rubro" },
];

const MODOS_INGRESO = MODOS_GASTO.filter((m) => m.id !== "pct_ventas");

function modoInicial(rubro, guardados) {
  if (guardados?.[rubro.id]) return guardados[rubro.id];
  return rubro.naturaleza === "ingreso" ? "variacion_anio_anterior" : "pct_ventas";
}

export default function ConfiguracionDatosTab({ clientId, ejercicio, rubros, puedeEscribir }) {
  const [config, setConfig] = useState(null);
  const [modos, setModos] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      setModos({});
      return;
    }
    let vivo = true;
    obtenerProyeccionConfig({ clientId, ejercicio: Number(ejercicio) })
      .then(({ data }) => {
        if (!vivo) return;
        setConfig(data);
        const siguiente = {};
        (rubros || []).forEach((rubro) => {
          siguiente[rubro.id] = modoInicial(rubro, data?.rubros_modo);
        });
        setModos(siguiente);
      })
      .catch(() => {
        if (vivo) setConfig(null);
      });
    return () => { vivo = false; };
  }, [clientId, ejercicio, rubros]);

  const congelado = Boolean(config?.congelado);

  const guardar = async () => {
    if (!puedeEscribir || !clientId || congelado) return;
    setGuardando(true);
    try {
      const { data } = await guardarProyeccionConfig({
        client_id: clientId,
        ejercicio: Number(ejercicio),
        rubros_modo: modos,
      });
      setConfig(data);
      toast.success("Se guardó el método de proyección de cada rubro.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar la configuración.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tab-configuracion-datos">
      <section className="space-y-2" data-testid="seccion-rangos">
        <h3 className="text-sm font-semibold text-[#0F2D5C]">Rangos de referencia por giro</h3>
        <p className="text-xs text-slate-600">
          Estos rangos alimentan el semáforo de Gastos razonables. El giro del contribuyente
          se propone con las actividades de la constancia ya guardada y se busca en esta tabla.
          La tabla es un ejemplo editable, no un criterio oficial del SAT.
        </p>
        <ReferenciasTable rubros={rubros} puedeEscribir={puedeEscribir} />
      </section>

      <section className="space-y-3" data-testid="seccion-metodos">
        <h3 className="text-sm font-semibold text-[#0F2D5C]">Cómo se proyecta cada rubro</h3>
        <p className="text-xs text-slate-600">
          El tope de cualquier variación es de −100 % a +200 %. Si no hay año anterior,
          se usa la variación promedio entre los meses cargados. Proyectado al cierre toma estos métodos.
        </p>
        {congelado ? (
          <p className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950" data-testid="escenario-congelado">
            Este escenario ya se adjuntó al expediente y quedó congelado. No se puede editar.
          </p>
        ) : null}
        {!clientId ? (
          <p className="text-sm text-slate-500">Seleccione una empresa para guardar el método de cada rubro.</p>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-2 py-2 text-left">Rubro</th>
                  <th className="px-2 py-2 text-left">Método</th>
                </tr>
              </thead>
              <tbody>
                {(rubros || []).map((rubro) => {
                  const opciones = rubro.naturaleza === "ingreso" ? MODOS_INGRESO : MODOS_GASTO;
                  return (
                    <tr key={rubro.id} className="border-t border-slate-100">
                      <td className="px-2 py-2">{rubro.etiqueta}</td>
                      <td className="px-2 py-2">
                        <select
                          className="rounded-sm border px-2 py-1"
                          disabled={!puedeEscribir || congelado}
                          value={modos[rubro.id] || modoInicial(rubro, modos)}
                          onChange={(e) => setModos({ ...modos, [rubro.id]: e.target.value })}
                          data-testid={`modo-${rubro.id}`}
                        >
                          {opciones.map((op) => (
                            <option key={op.id} value={op.id}>{op.etiqueta}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <button
          type="button"
          className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          disabled={!puedeEscribir || !clientId || congelado || guardando}
          onClick={guardar}
          data-testid="btn-guardar-metodos"
        >
          {guardando ? "Guardando…" : "Guardar métodos"}
        </button>
      </section>
    </div>
  );
}
